/* Mobile product-card media: two surfaces, two deliberately different models.
 *
 * RAIL cards (homepage) - this file owns the horizontal gesture, because an
 * inner native scroller fights the outer product rail (see below).
 *
 * NON-RAIL cards (collection, search) - the theme's own native scroller keeps
 * the gesture; this file only adds the same chevron affordance on top of it
 * and reads the current frame back out of the scroll position. See
 * attachNative(). The difference is intentional, not an inconsistency: the
 * rail conflict that forced the custom model simply does not exist there.
 *
 * Why this exists instead of the theme's own touch path:
 * `.supports-touch hover-images .product-item__bg__slider` is
 * `overflow-x: auto; scroll-snap-type: x mandatory`, which inside a horizontal
 * rail creates two native scrollers on the same axis. Measured on a real
 * iPhone that made the card eat the gesture: the photo flipped to its second
 * frame and the rail did not advance. So the nested native scroller stays off
 * in rails (assets/theme.css, MOBILE-RAIL-002) and the swipe is driven here as
 * gesture INTENT instead - no second scroll container, no carousel library.
 *
 * Scope, deliberately narrow: current slide index, pointer tracking, active
 * slide, the existing progress indicator, edge hand-off to the rail, and
 * suppressing the click a drag would otherwise produce. It does not touch
 * variants, Quick Add, cart, rail rendering or the desktop Flickity path.
 *
 * Edge hand-off keeps the rail reachable from the photo itself: swiping past
 * the last frame moves the rail to the next product, past the first frame to
 * the previous one, so a customer can browse images and products with one
 * continuous gesture and never reaches a dead end.
 */
(function () {
  'use strict';

  if (window.__definexCardMediaSwipeBound) return;
  window.__definexCardMediaSwipeBound = true;

  // build: card-media-swipe r7 (adds non-rail native-scroller nav)

  var RAIL_SELECTOR = '.grid--mobile-rail, .grid--mobile-slider, .community-stl__rail, .cart-recs__rail';
  var SLIDE_SELECTOR = '[data-hover-slide-touch]';
  var ACTIVE_CLASS = 'is-card-media-active';
  var READY_ATTR = 'data-card-media';
  // Separate marker for the non-rail surfaces (collection, search). It must NOT
  // be READY_ATTR: every rail rule in assets/theme.css keys off
  // `hover-images[data-card-media]` to switch the card to the one-frame-at-a-time
  // model, and those cards must keep the theme's own native scroller.
  var NAV_READY_ATTR = 'data-card-media-nav';
  // Past this much movement the gesture has declared an axis.
  var AXIS_THRESHOLD = 8;
  // Below this a horizontal movement is a sloppy tap, not a swipe.
  var SWIPE_THRESHOLD = 40;

  function slidesOf(host) {
    var touch = typeof window.matchMedia === 'function' &&
      window.matchMedia('(hover: none), (pointer: coarse)').matches;
    return Array.prototype.filter.call(host.querySelectorAll(SLIDE_SELECTOR), function (s) {
      return !s.hasAttribute('data-colour-hidden') &&
        (!touch || !s.hasAttribute('data-card-colour-hidden'));
    });
  }

  function setIndex(host, index) {
    var slides = slidesOf(host);
    if (!slides.length) return;
    var next = Math.max(0, Math.min(index, slides.length - 1));
    slides.forEach(function (slide, i) {
      slide.classList.toggle(ACTIVE_CLASS, i === next);
    });
    host.style.setProperty('--slides-count', slides.length);
    host.style.setProperty('--slider-index', next);
    host.dataset.cardMediaIndex = String(next);
    syncNav(host, next, slides.length);
    preload(slides[next]);
    preload(slides[next + 1]);
    preload(slides[next - 1]);
  }

  // MOBILE-CARD-MEDIA-AFFORDANCE-001
  // Two small chevrons so a shopper can SEE that a card holds more than one
  // photo, and reach them without dragging (the single-pointer alternative
  // WCAG 2.2 wants for a drag interaction). They drive the same index as the
  // swipe - no second slider, no second state, no library, no new markup in
  // Liquid and no extra image request.
  //
  // Deliberately NOT inside the product link: the photo is wrapped by
  // `a.product-link`, so buttons built in there would be interactive elements
  // nested in an anchor. They are appended to `.product-item__image`, the same
  // container the Quick Add trigger already uses, as siblings of the anchor.
  function navStrings() {
    var s = (window.theme && window.theme.strings) || {};
    return {
      prev: s.mediaPrevious || 'Önceki görsel',
      next: s.mediaNext || 'Sonraki görsel'
    };
  }

  function buildNav(host, step) {
    // DEV-QUICK-ADD-FINAL-MEDIA-002 — merchant decision: no arrow overlay on
    // standard product cards. The chevrons this function built were the
    // right-arrow in the merchant's screenshot
    // (button.card-media-nav__btn--next, 44x44, "Sonraki görsel").
    //
    // Disabled at the source rather than hidden with CSS so the buttons are
    // never created at all: nothing to leave in the tab order or the
    // accessibility tree. Everything else in this controller is untouched -
    // swipe, the index model, neighbour preloading and the edge hand-off all
    // still work, and syncNav() below already no-ops when there is no nav.
    //
    // Scope is product cards only: this controller only ever runs on
    // `.product-item__image`; the PDP gallery is a different component and
    // keeps its own navigation.
    //
    // Trade-off worth stating: the chevrons were also the single-pointer
    // alternative to dragging (WCAG 2.2 2.5.7). With them gone, additional
    // card frames are reachable by swipe or by opening the product.
    return;
    /* eslint-disable no-unreachable */
    var frame = host.closest('.product-item__image');
    if (!frame || frame.querySelector('.card-media-nav')) return;
    var labels = navStrings();
    var nav = document.createElement('div');
    nav.className = 'card-media-nav';
    ['prev', 'next'].forEach(function (dir) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'card-media-nav__btn card-media-nav__btn--' + dir;
      b.setAttribute('aria-label', dir === 'prev' ? labels.prev : labels.next);
      b.dataset.cardMediaNav = dir;
      b.innerHTML =
        '<svg class="card-media-nav__icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false" width="16" height="16">' +
        '<path fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" d="' +
        (dir === 'prev' ? 'M15 4 L7 12 L15 20' : 'M9 4 L17 12 L9 20') +
        '"/></svg>';
      b.addEventListener('click', function (event) {
        // The buttons sit over the photo; without this the click would also
        // reach the card and open the product.
        event.preventDefault();
        event.stopPropagation();
        step(dir === 'next' ? 1 : -1);
      });
      // A press on a button is not the start of a swipe.
      b.addEventListener('pointerdown', function (event) {
        event.stopPropagation();
      });
      nav.appendChild(b);
    });
    frame.appendChild(nav);
    /* eslint-enable no-unreachable */
  }

  // First frame: no previous. Last frame: no next - the buttons stay literally
  // "previous/next IMAGE" and never turn into "next product", unlike the swipe
  // edge hand-off, which is a separate and deliberate behaviour.
  function syncNav(host, index, total) {
    var frame = host.closest('.product-item__image');
    var nav = frame && frame.querySelector('.card-media-nav');
    if (!nav) return;
    var prev = nav.querySelector('[data-card-media-nav="prev"]');
    var next = nav.querySelector('[data-card-media-nav="next"]');
    if (prev) prev.disabled = index <= 0;
    if (next) next.disabled = index >= total - 1;
  }

  // The frame a customer is about to reach must not arrive as a white box:
  // give the neighbouring slide's image a head start, one slide either side
  // only, so nothing is fetched for products or frames nobody swiped towards.
  function preload(slide) {
    if (!slide) return;
    var img = slide.querySelector('img');
    if (!img || img.dataset.cardMediaPreloaded) return;
    img.dataset.cardMediaPreloaded = '1';
    if (img.getAttribute('loading') === 'lazy') img.setAttribute('loading', 'eager');
    if (typeof img.decode === 'function') img.decode().catch(function () {});
  }

  function getIndex(host) {
    return parseInt(host.dataset.cardMediaIndex || '0', 10) || 0;
  }

  // Move the rail by real geometry, never by a guessed pixel step: the card's
  // own offsetLeft already accounts for card width, gap and the rail's start
  // padding at every breakpoint.
  function stepRail(host, direction) {
    var rail = host.closest(RAIL_SELECTOR);
    var card = host.closest('grid-item, .grid-item');
    if (!rail || !card) return false;
    var cards = Array.prototype.filter.call(rail.children, function (c) {
      return c.getBoundingClientRect().width > 0;
    });
    var at = cards.indexOf(card);
    if (at === -1) return false;
    var target = cards[at + direction];
    if (!target) return false;
    var left = target.offsetLeft - (cards[0] ? cards[0].offsetLeft : 0);
    rail.scrollTo({left: left, behavior: 'smooth'});
    return true;
  }

  /* ------------------------------------------------------------------
   * MOBILE-NONRAIL-MEDIA-AFFORDANCE-001 - collection and search cards.
   *
   * These cards are NOT inside a horizontal product rail, so the conflict that
   * forced the rail cards onto this controller does not exist here: the
   * theme's own touch path is valid and stays in charge. On touch,
   * `.supports-touch hover-images .product-item__bg__slider` is
   * `overflow-x: auto; scroll-snap-type: x mandatory` with `scroll-snap-align:
   * start` slides, and HoverImages.handleScroll() (assets/theme.js) already
   * derives the progress indicator from `scrollLeft / clientWidth`.
   *
   * So all that is added here is the same chevron affordance the rails got,
   * driven BY that scroller: a tap scrolls the existing container to the next
   * slide's real offset, and the button states are derived back from the
   * resulting scroll position. There is no second index, no state machine, no
   * touch-action change, no snap change and no new markup in Liquid - a finger
   * swipe still moves the same container the same way it always did.
   * ------------------------------------------------------------------ */
  function nativeSlider(host) {
    return host.querySelector('.product-item__bg__slider');
  }

  // Current frame from real geometry, never from a stored counter: whichever
  // slide's own start is closest to where the scroller actually sits. Works at
  // any card width and survives a partially-settled snap.
  function nativeIndex(slider, slides) {
    var base = slides[0] ? slides[0].offsetLeft : 0;
    var at = slider.scrollLeft;
    var best = 0;
    var bestDistance = Infinity;
    for (var i = 0; i < slides.length; i++) {
      var distance = Math.abs(slides[i].offsetLeft - base - at);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = i;
      }
    }
    return best;
  }

  function attachNative(host) {
    if (host.hasAttribute(NAV_READY_ATTR)) return;
    var slider = nativeSlider(host);
    if (!slider) return;
    var slides = slidesOf(host);
    if (slides.length < 2) return;

    host.setAttribute(NAV_READY_ATTR, '');

    function sync() {
      var current = slidesOf(host);
      if (!current.length) return;
      syncNav(host, nativeIndex(slider, current), current.length);
    }

    buildNav(host, function (direction) {
      var current = slidesOf(host);
      var target = current[nativeIndex(slider, current) + direction];
      if (!target) return;
      var base = current[0] ? current[0].offsetLeft : 0;
      // Hand the move to the existing scroller and let its own snapping
      // finish it - no manual animation, no pixel guess.
      slider.scrollTo({left: target.offsetLeft - base, behavior: 'smooth'});
      preload(current[nativeIndex(slider, current) + direction * 2]);
    });

    // One listener, rAF-coalesced: the states follow the scroller whether it
    // was moved by a finger or by a button, with no timer deciding when the
    // gesture "ended".
    var queued = false;
    slider.addEventListener(
      'scroll',
      function () {
        if (queued) return;
        queued = true;
        requestAnimationFrame(function () {
          queued = false;
          sync();
        });
      },
      {passive: true}
    );

    sync();
  }

  function attach(host) {
    if (host.hasAttribute(READY_ATTR)) return;
    var slides = slidesOf(host);
    if (slides.length < 2) return;
    if (!host.closest(RAIL_SELECTOR)) {
      attachNative(host);
      return;
    }

    host.setAttribute(READY_ATTR, '');
    buildNav(host, function (direction) {
      setIndex(host, getIndex(host) + direction);
    });
    setIndex(host, 0);

    var startX = 0;
    var startY = 0;
    var axis = null;
    var tracking = false;
    var swiped = false;
    var committedThisGesture = false;

    host.addEventListener(
      'pointerdown',
      function (event) {
        if (event.pointerType === 'mouse') return;
        tracking = true;
        axis = null;
        committedThisGesture = false;
        startX = event.clientX;
        startY = event.clientY;
      },
      {passive: true}
    );

    host.addEventListener(
      'pointermove',
      function (event) {
        if (!tracking || axis === 'y') return;
        var dx = event.clientX - startX;
        var dy = event.clientY - startY;
        if (axis === null) {
          if (Math.abs(dx) < AXIS_THRESHOLD && Math.abs(dy) < AXIS_THRESHOLD) return;
          // Vertical wins ties: the page must keep scrolling from the photo.
          axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        }
      },
      {passive: true}
    );

    // Pointer Events and Touch Events both fire for one finger on iOS, so the
    // commit is shared and guarded PER GESTURE, not on a timer: whichever path
    // arrives first wins and the other is a no-op until the next finger-down.
    // A time window was tried first and was wrong - at 350ms it swallowed a
    // genuine second swipe, which is exactly how a customer browses frames.
    function commit(dx) {
      if (committedThisGesture) return;
      committedThisGesture = true;
      // Arm suppression for the compatibility click this gesture will produce.
      // Armed at commit, disarmed by the next interaction, so it can only ever
      // swallow a click belonging to THIS gesture.
      swiped = true;
      var index = getIndex(host);
      var slideCount = slidesOf(host).length;
      if (dx < 0) {
        if (index < slideCount - 1) setIndex(host, index + 1);
        else stepRail(host, 1);
      } else {
        if (index > 0) setIndex(host, index - 1);
        else stepRail(host, -1);
      }
    }

    function end(event) {
      if (!tracking) return;
      tracking = false;
      if (axis !== 'x') return;
      var dx = event.clientX - startX;
      if (Math.abs(dx) < SWIPE_THRESHOLD) return;
      commit(dx);
    }

    host.addEventListener('pointerup', end, {passive: true});

    // iOS hands a gesture to a scrolling ancestor by CANCELLING the pointer
    // stream. Dropping the gesture there is what left the photo unchanged
    // while the rail moved. If horizontal intent was already established the
    // swipe is honoured on cancel instead of discarded.
    host.addEventListener(
      'pointercancel',
      function (event) {
        if (tracking && axis === 'x') end(event);
        tracking = false;
      },
      {passive: true}
    );

    // Touch Events in parallel with Pointer Events, for one reason only:
    // `touchmove` can be non-passive, so once the gesture is known to be
    // horizontal we can preventDefault() and stop the rail claiming it. A
    // `touch-action: pan-y` declaration alone did not hold on a real iPhone -
    // the rail scrolled and the card media never moved. preventDefault is
    // never called before the axis is known, so vertical page scrolling from
    // the photo is untouched.
    var tStartX = 0;
    var tStartY = 0;
    var tAxis = null;
    var tActive = false;

    host.addEventListener(
      'touchstart',
      function (event) {
        if (event.touches.length !== 1) {
          tActive = false;
          return;
        }
        tActive = true;
        tAxis = null;
        committedThisGesture = false;
        tStartX = event.touches[0].clientX;
        tStartY = event.touches[0].clientY;
      },
      {passive: true}
    );

    host.addEventListener(
      'touchmove',
      function (event) {
        if (!tActive || event.touches.length !== 1) return;
        var dx = event.touches[0].clientX - tStartX;
        var dy = event.touches[0].clientY - tStartY;
        if (tAxis === null) {
          if (Math.abs(dx) < AXIS_THRESHOLD && Math.abs(dy) < AXIS_THRESHOLD) return;
          tAxis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        }
        if (tAxis === 'x' && event.cancelable) event.preventDefault();
      },
      {passive: false}
    );

    function touchEnd(event) {
      if (!tActive) return;
      tActive = false;
      if (tAxis !== 'x') return;
      var touch = (event.changedTouches && event.changedTouches[0]) || null;
      if (!touch) return;
      var dx = touch.clientX - tStartX;
      if (Math.abs(dx) < SWIPE_THRESHOLD) return;
      commit(dx);
    }

    host.addEventListener('touchend', touchEnd, {passive: true});
    host.addEventListener('touchcancel', touchEnd, {passive: true});

    // A drag must never open the product or Quick Add. The listener sits on
    // the CARD, not on the media element: the product link wraps the photo, so
    // a click lands on an ancestor of `host` and a listener bound to `host`
    // would never see it (measured - the link still fired after a swipe).
    // Only the click a real swipe produces is swallowed; an ordinary tap is
    // left alone.
    var clickScope = host.closest('grid-item, .grid-item') || host;

    // MOBILE-CARD-TAP-001. A new press anywhere on this card starts a NEW
    // interaction, so suppression still armed from the previous swipe is stale
    // and must go. It used to be cleared only by a press on the photo, so a
    // deliberate tap starting on the Quick Add trigger, the product link or a
    // swatch inherited the last swipe's suppression and was eaten - measured
    // 2/2 on staging, while the same tap with no preceding swipe opened first
    // time. Scoped to this card: no document-level listener, and one card's
    // gesture can never disarm another's.
    function startInteraction() {
      swiped = false;
    }
    clickScope.addEventListener('pointerdown', startInteraction, true);
    clickScope.addEventListener('touchstart', startInteraction, {capture: true, passive: true});

    clickScope.addEventListener(
      'click',
      function (event) {
        if (!swiped) return;
        swiped = false;
        event.preventDefault();
        event.stopPropagation();
      },
      true
    );
  }

  function init(root) {
    // No capability gate here on purpose. theme.js swaps `no-touch` for
    // `supports-touch` during its own init, and this file is deferred, so
    // reading that class at attach time is a race that loses on a real phone
    // exactly when it matters. Binding is harmless without touch: every
    // handler ignores `pointerType === 'mouse'`, and the CSS that depends on
    // [data-card-media] is already scoped to rails below 990px.
    var scope = root && root.querySelectorAll ? root : document;
    if (scope.matches && scope.matches('hover-images')) attach(scope);
    scope.querySelectorAll('hover-images').forEach(attach);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  // AJAX renderers announce only the subtree whose cards were replaced.
  document.addEventListener('theme:cards:rendered', function (event) {
    init(event.target);
  });

  // The swatch adapter changes only the eligible frames. Reset the EXISTING
  // rail index or native scroll position, without adding another controller.
  document.addEventListener('theme:card:colour-selected', function (event) {
    var card = event.target;
    if (!card || !card.querySelectorAll) return;
    card.querySelectorAll('hover-images').forEach(function (host) {
      var slides = slidesOf(host);
      var slider = nativeSlider(host);
      if (slider) slider.scrollLeft = 0;
      host.style.setProperty('--slides-count', Math.max(1, slides.length));
      host.style.setProperty('--slider-index', 0);
      if (host.hasAttribute(READY_ATTR)) setIndex(host, 0);
      else preload(slides[0]);
    });
  });

  // Theme Editor re-renders sections; attach() is idempotent via READY_ATTR so
  // a reload cannot double-bind a card that survived.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
